import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, MoreHorizontal, Pencil, Trash2, Car, Home, Briefcase, FileText, ArrowUpDown, ArrowUp, ArrowDown, Download, Eye, ExternalLink, RefreshCw, Archive, Settings, Copy, ChevronLeft, ChevronRight, ShieldPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const policyTypeLabels = {
  auto: 'Veicolo',
  casa: 'Casa',
  attivita: 'Attività',
  vita: 'Vita',
  salute: 'Salute',
  rc_professionale: 'RC Prof.'
};

const policyTypeIcons = {
  auto: Car,
  casa: Home,
  attivita: Briefcase,
  vita: FileText,
  salute: ShieldPlus,
  rc_professionale: Briefcase
};

const statusLabels = {
  attiva: 'Attiva',
  sospesa: 'Sospesa',
  interrotta: 'Interrotta',
  scaduta: 'Scaduta',
  archiviata: 'Archiviata',
  rinnovata: 'Rinnovata'
};

const statusColors = {
  attiva: 'bg-green-100 text-green-700',
  sospesa: 'bg-amber-100 text-amber-700',
  interrotta: 'bg-red-100 text-red-700',
  scaduta: 'bg-slate-100 text-slate-700',
  archiviata: 'bg-slate-200 text-slate-600',
  rinnovata: 'bg-blue-100 text-blue-700'
};

const Policies = () => {
  const [policies, setPolicies] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, attiva: 0, sospesa: 0, expiring_30d: 0, premium_attiva: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAgentSource, setFilterAgentSource] = useState('all');
  const [agentSources, setAgentSources] = useState([]);
  const [sortField, setSortField] = useState('end_date');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Dialog per rinnovo
  const [renewDialog, setRenewDialog] = useState(false);
  const [renewingPolicy, setRenewingPolicy] = useState(null);
  const [renewData, setRenewData] = useState({ start_date: '', end_date: '', premium_amount: '' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Reset a pagina 1 quando cambiano ricerca/filtri/ordinamento
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterType, filterAgentSource, sortField, sortDirection]);

  // Fetch paginato (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPolicies();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, filterType, filterAgentSource, sortField, sortDirection, currentPage]);

  useEffect(() => {
    fetchAgentSources();
    fetchStats();
  }, []);

  const fetchAgentSources = async () => {
    try {
      const response = await axios.get(`${API}/agent-sources`);
      setAgentSources(response.data.map(s => s.name).filter(Boolean));
    } catch (error) {
      console.error('Error fetching agent sources:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/policies/stats`);
      setStats(response.data);
    } catch (error) {
      // silenzioso: le card mostreranno gli ultimi valori noti
    }
  };

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('policy_type', filterType);
      if (filterAgentSource !== 'all') params.append('agent_source', filterAgentSource);
      params.append('page', currentPage);
      params.append('page_size', ITEMS_PER_PAGE);
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);

      const response = await axios.get(`${API}/policies?${params.toString()}`);
      setPolicies(response.data.items);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Errore nel caricamento delle polizze');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(endDate);
    expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadge = (policy) => {
    if (policy.status !== 'attiva') return null;
    const days = getDaysUntilExpiry(policy.end_date);
    if (days === null) return null;
    if (days < 0) return <Badge className="bg-red-600 text-white ml-2">Scaduta</Badge>;
    if (days <= 7) return <Badge className="bg-red-100 text-red-700 ml-2">Urgente</Badge>;
    if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 ml-2">In scadenza</Badge>;
    return null;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-emerald-600" /> : 
      <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  // Helper per generare URL di navigazione (per link nativi <a>)
  const getClientUrl = (clientId) => `/clients?open=${clientId}`;
  
  const getAssetUrl = (policy) => {
    if (policy.asset_id && policy.client_id) {
      return `/clients?open=${policy.client_id}&asset=${policy.asset_id}`;
    }
    return '#';
  };
  
  const getManagePolicyUrl = (policy) => {
    if (policy.asset_id && policy.client_id) {
      return `/clients?open=${policy.client_id}&asset=${policy.asset_id}&manage=${policy.id}`;
    }
    return '#';
  };

  // Apri dialog rinnovo
  const openRenewDialog = (policy) => {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);
    
    setRenewingPolicy(policy);
    setRenewData({
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      premium_amount: policy.premium_amount?.toString() || ''
    });
    setRenewDialog(true);
  };

  // Esegui rinnovo
  const handleRenew = async () => {
    if (!renewingPolicy || !renewData.start_date || !renewData.end_date) {
      toast.error('Compila tutti i campi');
      return;
    }
    try {
      // Crea nuova polizza come copia
      const newPolicy = {
        client_id: renewingPolicy.client_id,
        asset_id: renewingPolicy.asset_id,
        policy_type: renewingPolicy.policy_type,
        policy_number: '',  // Vuoto - inserimento manuale dall'operatore
        company: renewingPolicy.company,
        agent: renewingPolicy.agent,
        payment_date: '',
        start_date: renewData.start_date,
        end_date: renewData.end_date,
        payment_frequency: renewingPolicy.payment_frequency || 'annuale',
        driving_type: renewingPolicy.driving_type || 'esperta',
        premium_amount: parseFloat(renewData.premium_amount) || renewingPolicy.premium_amount,
        coverage_details: renewingPolicy.coverage_details,
        status: 'attiva'
      };
      
      await axios.post(`${API}/policies`, newPolicy);
      
      // Segna la vecchia polizza come rinnovata
      await axios.patch(`${API}/policies/${renewingPolicy.id}/status`, {
        renewal_status: 'rinnovata'
      });
      
      toast.success('Polizza rinnovata con successo');
      setRenewDialog(false);
      setRenewingPolicy(null);
      fetchPolicies();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il rinnovo');
    }
  };

  // Archivia polizza
  const handleArchive = async (policy) => {
    if (!window.confirm('Sei sicuro di voler archiviare questa polizza?')) return;
    try {
      await axios.patch(`${API}/policies/${policy.id}/status`, {
        status: 'archiviata'
      });
      toast.success('Polizza archiviata');
      fetchPolicies();
      fetchStats();
    } catch (error) {
      toast.error('Errore durante l\'archiviazione');
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa polizza?')) return;
    try {
      await axios.delete(`${API}/policies/${policyId}`);
      toast.success('Polizza eliminata');
      fetchPolicies();
      fetchStats();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // `policies` \u00e8 gi\u00e0 la pagina corrente (paginazione server-side), `totalItems` il totale filtrato
  const paginatedPolicies = policies;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('policy_type', filterType);
      if (filterAgentSource !== 'all') params.append('agent_source', filterAgentSource);
      const response = await axios.get(`${API}/policies?${params.toString()}`);
      const allFiltered = response.data;

      const headers = ['Cliente', 'Bene', 'N. Polizza', 'Compagnia', 'Agenzia', 'Premio', 'Scadenza', 'Stato'];
      const rows = allFiltered.map(p => [
        p.client_name || '',
        p.asset_label || '-',
        p.policy_number || '',
        p.company || '',
        p.agent || '',
        p.premium_amount?.toFixed(2) || '0',
        formatDate(p.end_date),
        statusLabels[p.status] || p.status
      ]);
      const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `polizze_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Esportazione completata');
    } catch (error) {
      toast.error('Errore durante l\'esportazione');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Polizze
          </h1>
          <p className="text-slate-600 mt-1">Gestisci le polizze assicurative</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-policies">
          <Download className="w-4 h-4 mr-2" />
          Esporta CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per cliente, n. polizza, compagnia..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-policies"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="auto">Veicolo</SelectItem>
                <SelectItem value="casa">Casa</SelectItem>
                <SelectItem value="attivita">Attività</SelectItem>
                <SelectItem value="salute">Salute</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="attiva">Attiva</SelectItem>
                <SelectItem value="sospesa">Sospesa</SelectItem>
                <SelectItem value="scaduta">Scaduta</SelectItem>
                <SelectItem value="archiviata">Archiviata</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAgentSource} onValueChange={setFilterAgentSource}>
              <SelectTrigger className="w-44" data-testid="filter-agent-source">
                <SelectValue placeholder="Agente/Fonte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agentSources.map(agent => (
                  <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Attive</p>
            <p className="text-2xl font-bold">{stats.attiva}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Sospese</p>
            <p className="text-2xl font-bold">{stats.sospesa}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">In Scadenza</p>
            <p className="text-2xl font-bold">{stats.expiring_30d}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Premio Totale</p>
            <p className="text-xl font-bold">{formatCurrency(stats.premium_attiva)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nessuna polizza trovata
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold min-w-[180px]">Cliente</TableHead>
                    <TableHead className="font-semibold min-w-[220px]">Bene</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Compagnia</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Agente/Fonte</TableHead>
                    <TableHead className="font-semibold min-w-[100px]">
                      <button onClick={() => handleSort('premium_amount')} className="flex items-center gap-1 hover:text-emerald-600">
                        Premio {getSortIcon('premium_amount')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold min-w-[100px]">
                      <button onClick={() => handleSort('end_date')} className="flex items-center gap-1 hover:text-emerald-600">
                        Scadenza {getSortIcon('end_date')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-center min-w-[100px]">Stato</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPolicies.map((policy) => {
                    const Icon = policyTypeIcons[policy.policy_type] || FileText;
                    return (
                      <TableRow key={policy.id} className="hover:bg-slate-50" data-testid={`policy-row-${policy.id}`}>
                        <TableCell>
                          <Link
                            to={getClientUrl(policy.client_id)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                          >
                            <span className="font-medium text-slate-900 group-hover:text-blue-600">
                              {policy.client_name || '-'}
                            </span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </Link>
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          <Link
                            to={getAssetUrl(policy)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full no-underline"
                          >
                            <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                              policy.policy_type === 'auto' ? 'bg-blue-100 text-blue-600' :
                              policy.policy_type === 'casa' ? 'bg-green-100 text-green-600' :
                              'bg-purple-100 text-purple-600'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                              {policy.asset_label || '-'}
                            </span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </Link>
                        </TableCell>
                        <TableCell className="text-slate-600">{policy.company || '-'}</TableCell>
                        <TableCell className="text-slate-600">{policy.client_agent_source || '-'}</TableCell>
                        <TableCell className="font-medium text-emerald-700">
                          {formatCurrency(policy.premium_amount)}{policy.payment_frequency === 'semestrale' ? '/sem' : '/anno'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <span className="text-slate-600">{formatDate(policy.end_date)}</span>
                            {getExpiryBadge(policy)}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={statusColors[policy.status]}>
                            {statusLabels[policy.status] || policy.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`policy-menu-${policy.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={getAssetUrl(policy)} className="flex items-center no-underline w-full cursor-pointer">
                                  <Eye className="w-4 h-4 mr-2" />
                                  Dettagli
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={getAssetUrl(policy)} className="flex items-center no-underline w-full cursor-pointer">
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Modifica
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={getManagePolicyUrl(policy)} className="flex items-center no-underline w-full cursor-pointer">
                                  <Settings className="w-4 h-4 mr-2" />
                                  Gestioni
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {policy.status === 'attiva' && (
                                <>
                                  <DropdownMenuItem onClick={() => openRenewDialog(policy)}>
                                    <RefreshCw className="w-4 h-4 mr-2 text-blue-600" />
                                    Rinnova
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleArchive(policy)}>
                                    <Archive className="w-4 h-4 mr-2 text-amber-600" />
                                    Archivia
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(policy.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} polizze
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, idx, arr) => {
                          const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsisBefore && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-slate-900' : ''}`}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              
              {totalPages === 1 && totalItems > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItems} polizz{totalItems === 1 ? 'a' : 'e'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Renew Dialog */}
      <Dialog open={renewDialog} onOpenChange={setRenewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Rinnova Polizza
            </DialogTitle>
            <DialogDescription>
              Crea una nuova polizza con le stesse caratteristiche.
            </DialogDescription>
          </DialogHeader>
          
          {renewingPolicy && (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Polizza attuale:</span> <span className="font-medium">{renewingPolicy.policy_number}</span></p>
                <p><span className="text-slate-500">Cliente:</span> <span className="font-medium">{renewingPolicy.client_name}</span></p>
                <p><span className="text-slate-500">Compagnia:</span> <span className="font-medium">{renewingPolicy.company}</span></p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Decorrenza *</Label>
                  <DateInput
                    value={renewData.start_date}
                    onChange={(val) => setRenewData({...renewData, start_date: val})}
                    placeholder="gg/mm/aaaa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Scadenza *</Label>
                  <DateInput
                    value={renewData.end_date}
                    onChange={(val) => setRenewData({...renewData, end_date: val})}
                    placeholder="gg/mm/aaaa"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Premio (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={renewData.premium_amount}
                  onChange={(e) => setRenewData({...renewData, premium_amount: e.target.value})}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRenewDialog(false)}>
                  Annulla
                </Button>
                <Button onClick={handleRenew}>
                  <Copy className="w-4 h-4 mr-2" />
                  Crea Rinnovo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Policies;
